/* eslint-disable max-lines-per-function */
import {
  Checkbox,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ListHeader from "./ListHeader";
import ListPagination from "./ListPagination";
import {
  AUDITS_PATH,
  ECOMMERCE,
  MARKETPLACE,
  MERCHANT,
  OWN_FLEET,
  OWN_FLEET_LABEL,
  SLA_ALERT_LEVEL_CONFIG,
  SPANISH_SERVICES,
} from "../../utils/constants";
import { IValuesArrayByEntity } from "../../interfaces/common";
import { ITableOrders, IMappedOrder, IPackage } from "../../interfaces/order";
import {
  mongoTimeStampToString,
  getOrderStatus,
  capitalizeOnlyFirstLetter,
  getChoppedText,
} from "../../utils/orders";
import ListOrdersActions from "./ListOrdersActions";
import { ITableHeaderColumn } from "../../interfaces/table";
import { getFeeString, isCaseInsensitiveEqual } from "../../utils/text";
import { hasAvailableStatusTransition } from "../../utils/orderStatus";
import { PackageBadge } from "../orders/PackageBadge";

const commonHeader: ITableHeaderColumn[] = [
  { label: "Origen" },
  { label: "Paquetes" },
  { label: "Costo" },
  { label: "Estado" },
  { label: "Estado del SLA" },
  { label: "Tipo" },
  { label: "Acciones" },
];
const merchantEcommerceHeader = [
  { label: "Operador" },
  { label: "Fecha" },
  { label: "N° de guía" },
  ...commonHeader,
];
const headerByEntity: IValuesArrayByEntity = {
  marketplace: [
    { label: "Operador" },
    { label: "Fecha" },
    { label: "N° de guía" },
    { label: "Merchant" },
    ...commonHeader,
  ],
  merchant: merchantEcommerceHeader,
  ecommerce: merchantEcommerceHeader,
};

const STATUS_COLUMN_WIDTH = 116;
const SLA_COLUMN_WIDTH = 122;

const getHeader = (entity: string, isOwnFleet: boolean, isAudit: boolean) => {
  if (isOwnFleet) {
    // Si es ownFleet, arma el header específico
    const markeplaceTableHeaders = [
      { label: "Operador" },
      { label: "Fecha" },
      { label: "ID Externo" },
      { label: "N° de guía" },
      { label: "Merchant" },
    ];
    const merchantTableHeaders = [
      { label: "Operador" },
      { label: "Fecha" },
      { label: "ID Externo" },
      { label: "N° de guía" },
    ];
    const ownFleetHeader = [
      ...(entity === MARKETPLACE
        ? markeplaceTableHeaders
        : merchantTableHeaders),
      ...commonHeader,
    ];
    return ownFleetHeader;
  }
  // Si no es ownFleet, usa el header por entidad y quita "Operador" si es auditoría
  const baseHeader = headerByEntity[entity] || [];
  return isAudit
    ? baseHeader.filter(({ label }) => label !== "Operador")
    : baseHeader;
};

const getCarrierName = (
  carrierName: string,
  orderInfo?: { custom_carrier_name?: string },
) => {
  const customCarrierName = orderInfo?.custom_carrier_name;
  if (carrierName === OWN_FLEET)
    return capitalizeOnlyFirstLetter(customCarrierName || OWN_FLEET_LABEL);
  return capitalizeOnlyFirstLetter(carrierName);
};

const ListOrders = ({
  orders,
  entity,
  handlePageChange,
  handleSelectedAction,
  pagination: { count, page },
  showActions,
  rules,
  onCheckOrder,
  onCheckAll,
  selectedOrders,
  availableSorts,
  appliedSort,
  handleChangeFilter,
}: ITableOrders) => {
  const isOwnFleet = orders.every((o) =>
    isCaseInsensitiveEqual(o.carrier, OWN_FLEET),
  );
  const isAudit = window.location.pathname.includes(AUDITS_PATH);
  const header = getHeader(entity, isOwnFleet, isAudit);
  const enableCheckOrders =
    onCheckOrder && (entity === MERCHANT || entity === ECOMMERCE);

  return (
    <TableContainer component={Paper} className="m-t-40 br-20 bg-light">
      <Table>
        <ListHeader
          columns={header}
          onCheckAll={enableCheckOrders ? onCheckAll : undefined}
        />
        <TableBody>
          {orders.map((row: IMappedOrder, index: number) => {
            const {
              _id,
              updatedAt,
              tracking_id,
              carrier,
              merchant_name,
              origin,
              packages,
              status,
              fee,
              service,
              alias,
              shipment_units,
              sla_alert_level,
              orderInfo,
            } = row;
            const slaConfig = sla_alert_level ? SLA_ALERT_LEVEL_CONFIG[sla_alert_level] : undefined;
            const orderStatus = getOrderStatus(status);
            const statusLabel = alias || orderStatus?.label || "-";
            const totalPackages = packages?.reduce(
              (accum: number, { quantity }: IPackage) => {
                // eslint-disable-next-line no-param-reassign
                accum += quantity;
                return accum;
              },
              0,
            );
            const isDisabled = !hasAvailableStatusTransition(
              service,
              status,
              isOwnFleet,
            );

            const showOptimizationBadge =
              shipment_units &&
              shipment_units.length > 0 &&
              !shipment_units.every((item) => !item.used_virtual_box_id);

            return (
              <TableRow key={index}>
                {enableCheckOrders && (
                  <TableCell className="p-l-20">
                    <Checkbox
                      sx={{
                        width: 2,
                        height: 2,
                        opacity: isDisabled ? 0.2 : 1,
                      }}
                      checked={
                        !!selectedOrders?.find(
                          ({ _id: targetId }) => targetId === _id,
                        )
                      }
                      disabled={isDisabled}
                      onChange={() => onCheckOrder(row)}
                    />
                  </TableCell>
                )}
                {!isAudit && (
                  <TableCell>{getCarrierName(carrier, orderInfo)}</TableCell>
                )}
                <TableCell className={onCheckOrder ? "" : "p-l-20"}>
                  {mongoTimeStampToString(updatedAt, true)}
                </TableCell>
                {/* ID Externo */}
                {isOwnFleet && (
                  <TableCell>{orderInfo?.external_id || "-"}</TableCell>
                )}
                <TableCell>
                  {orderInfo?.custom_tracking_id || tracking_id}
                </TableCell>
                {entity === MARKETPLACE && merchant_name && (
                  <Tooltip title={merchant_name} placement="top-start">
                    <TableCell>{getChoppedText(20, merchant_name)}</TableCell>
                  </Tooltip>
                )}
                <Tooltip title={origin} placement="top-start">
                  <TableCell>{origin && getChoppedText(25, origin)}</TableCell>
                </Tooltip>
                <TableCell>
                  {totalPackages}
                  {showOptimizationBadge && <PackageBadge packages={shipment_units} />}
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  {getFeeString(fee)}
                </TableCell>
                <TableCell
                  sx={{
                    width: STATUS_COLUMN_WIDTH,
                    minWidth: STATUS_COLUMN_WIDTH,
                    maxWidth: STATUS_COLUMN_WIDTH,
                    pr: 1,
                  }}
                >
                  <Tooltip title={statusLabel} placement="top-start">
                    <Typography noWrap>{statusLabel}</Typography>
                  </Tooltip>
                </TableCell>
                <TableCell
                  sx={{
                    width: SLA_COLUMN_WIDTH,
                    minWidth: SLA_COLUMN_WIDTH,
                    whiteSpace: "nowrap",
                    pl: 1,
                  }}
                >
                  {slaConfig ? (
                    <Chip
                      label={slaConfig.label}
                      size="small"
                      sx={{ backgroundColor: slaConfig.color, color: "#fff", fontWeight: 600 }}
                    />
                  ) : "-"}
                </TableCell>
                <TableCell>
                  {SPANISH_SERVICES[service] ||
                    SPANISH_SERVICES[orderInfo?.service]}
                </TableCell>
                <ListOrdersActions
                  row={row}
                  showActions={showActions}
                  handleSelectedAction={handleSelectedAction}
                  rules={rules}
                  entity={entity}
                />
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <ListPagination
        total={count}
        page={page}
        onChangePage={handlePageChange}
        availableSorts={availableSorts}
        appliedSort={appliedSort}
        handleChangeFilter={handleChangeFilter}
      />
    </TableContainer>
  );
};

export default ListOrders;
